import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection, EventTypes } from 'constants/index'
import { caseInsensitiveSort, deleteBatchDocuments } from 'utils'
import { signOut } from 'store/auth'
import { chunk, first } from 'lodash'
import { sortDataArrayByCreatedAt } from 'utils/times'

export const ALL_DATES_FILTER = 'All Dates'

export const initialState = {
  error: null,
  inProgress: false,
  events: [],
  event: null,
  eventTypes: [],
  selectedDateFilter: ALL_DATES_FILTER,
  currentPage: 0,
  pageSize: 10
}

const eventSlice = createSlice({
  name: 'event',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setEvents: (state, action) => {
      state.events = action.payload
    },
    deleteEvent: (state, action) => {
      state.events = state.events.filter((event) => event.id !== action.payload)
    },
    setEventTypes: (state, action) => {
      state.eventTypes = action.payload
    },
    setEvent: (state, action) => {
      state.event = action.payload
    },
    setSelectedDateFilter: (state, action) => {
      state.selectedDateFilter = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.events = []
      state.event = null
      state.eventTypes = []
      state.selectedDateFilter = ALL_DATES_FILTER
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addEvent = (payload) => async (dispatch, getState) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    const user = getState().auth.user
    const doc = await FirestoreDatabase.collection(Collection.EVENTS).add({
      ...payload,
      conferenceId: user?.conferenceId
    })
    dispatch(eventSlice.actions.setProgress(false))
    return doc.id
  } catch (err) {
    console.error(err)
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const fetchEvents = async (conferenceId) => {
  try {
    const eventsSnap = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('startDate')
      .get()

    const events = sortDataArrayByCreatedAt(
      eventsSnap.docs?.map((doc) => ({ id: doc?.id, ...doc.data() }))
    ).map((item, i) => ({ ...item, srNo: i + 1 }))
    const pollsSnap = await FirestoreDatabase.collection(Collection.POLLS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const pollsData = pollsSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const pollsEvents = []
    pollsData?.forEach((doc) => {
      if (!pollsEvents.includes(doc?.eventId)) {
        pollsEvents.push(doc?.eventId)
      }
    })

    events.forEach((event) => {
      event.havePolls = pollsEvents?.includes(event.id)
    })
    return events
  } catch (err) {
    //do nothing
  }
}

export const fetchEventViaPINCode = (pinCode) => async (dispatch) => {
  try {
    const data = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('pinCode', '==', pinCode)
      .limit(1)
      .get()
    const doc = first(data?.docs)
    return { id: doc.id, ...doc.data() }
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
  }
}

export const fetchEventTypes = () => async (dispatch, getState) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    const user = getState().auth.user
    FirestoreDatabase.collection(Collection.EVENT_CARD_STYLES)
      .where('conferenceId', '==', user?.conferenceId)
      .onSnapshot((documents) => {
        const eventTypes = []
        documents.forEach((doc) => {
          eventTypes.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(eventSlice.actions.setEventTypes(caseInsensitiveSort(eventTypes, 'type')))
        dispatch(eventSlice.actions.setProgress(false))
      })
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const fetchRealTimeEvent = (eventId, callback) => {
  return FirestoreDatabase.collection(Collection.EVENTS)
    .doc(eventId)
    .onSnapshot((doc) => {
      const event = doc.exists ? { id: doc.id, ...doc.data() } : null
      callback(event)
    })
}

export const fetchEventById = (eventId) => async (dispatch) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).get()
    dispatch(
      eventSlice.actions.setEvent({
        id: snapshot.id,
        ...snapshot.data()
      })
    )
    dispatch(eventSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const removeSessionsAndTalks = async (eventId) => {
  try {
    const sessionsSnap = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('eventId', '==', eventId)
      .get()
    const sessions = sessionsSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))
    const sessionIdsList = sessions.map((session) => session?.id)

    const batchSize = 10
    const batchedSessionIds = chunk(sessionIdsList, batchSize)

    const sessionTalks = []
    for (const batchSessionIds of batchedSessionIds) {
      const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
        .where('sessionId', 'in', batchSessionIds)
        .get()
      talksSnap.docs.forEach((doc) => {
        sessionTalks.push({ id: doc?.id, ...doc.data() })
      })
    }

    const pollIdsList = sessionTalks
      .filter((talk) => talk.havePolls === true)
      .map((talk) => talk?.pollId)
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    sessions.forEach((session) => {
      if (session?.havePolls) {
        pollIdsList.push(session?.pollId)
      }
    })

    const pollIdsChunks = chunk(pollIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = pollIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((pollId) => {
        const docRef = pollsColRef.doc(pollId)
        batch.update(docRef, {
          alreadyUsed: false,
          eventId: null,
          sessionId: null,
          talkId: null
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)

    const talkRefs = sessionTalks.map(({ id }) =>
      FirestoreDatabase.collection(Collection.TALKS).doc(id)
    )
    const sessionRefs = sessions.map(({ id }) =>
      FirestoreDatabase.collection(Collection.SESSIONS).doc(id)
    )
    await Promise.all([deleteBatchDocuments(talkRefs), deleteBatchDocuments(sessionRefs)])
  } catch (err) {
    console.error(err)
  }
}

export const removeEventTalks = async (eventId) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .get()
    const talks = talksSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))

    const pollIdsList = talks.filter((talk) => talk.havePolls === true).map((talk) => talk?.pollId)
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    const pollIdsChunks = chunk(pollIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = pollIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((pollId) => {
        const docRef = pollsColRef.doc(pollId)
        batch.update(docRef, {
          alreadyUsed: false,
          eventId: null,
          sessionId: null,
          talkId: null
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)

    const talksRefs = talks.map(({ id }) => FirestoreDatabase.collection(Collection.TALKS).doc(id))
    await deleteBatchDocuments(talksRefs)
  } catch (err) {
    console.error(err)
  }
}

export const removeAbstractOrPosterNumberFromEventTalks = async (eventId) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .get()
    const talks = talksSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))

    const talkIdsList = talks.map((talk) => talk.id)
    const talksColRef = FirestoreDatabase.collection(Collection.TALKS)

    const talkIdsChunks = chunk(talkIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = talkIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((talkId) => {
        const docRef = talksColRef.doc(talkId)
        batch.update(docRef, {
          abstractNumber: null,
          posterNumber: null,
          isAbstract: false
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)
  } catch (err) {
    console.error(err)
  }
}

export const updateEventById = (eventId, payload) => async (dispatch) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.EVENTS)
      .doc(eventId)
      .update({
        ...payload
      })
    dispatch(eventSlice.actions.setProgress(false))
    return true
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const deleteEventById = (eventId) => async (dispatch) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))

    const eventSnap = await FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).get()
    const event = { id: eventSnap.id, ...eventSnap.data() }

    const eventPollId =
      event?.eventType === EventTypes.STANDALONE && event?.havePolls && event?.pollId
        ? event?.pollId
        : ''

    const sessionsSnap = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('eventId', '==', eventId)
      .get()
    const sessions = sessionsSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })
    const pollIdsListWithinSessions = sessions
      .filter((session) => session.havePolls === true)
      .map((session) => session.pollId)

    const sessionIds = sessions.map((session) => session.id)
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .where('sessionId', 'in', ['', ...sessionIds])
      .get()

    const talks = talksSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const pollIdsListWithinTalks = talks
      .filter((talk) => talk.havePolls === true)
      .map((talk) => talk?.pollId)

    const allPollIdsList = eventPollId
      ? [...pollIdsListWithinSessions, ...pollIdsListWithinTalks, eventPollId]
      : [...pollIdsListWithinSessions, ...pollIdsListWithinTalks]

    const batch = FirestoreDatabase.batch()
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    allPollIdsList.forEach((pollId) => {
      const docRef = pollsColRef.doc(pollId)
      batch.update(docRef, {
        alreadyUsed: false,
        eventId: null,
        sessionId: null,
        talkId: null
      })
    })

    talks.forEach(({ id }) => {
      const docRef = FirestoreDatabase.collection(Collection.TALKS).doc(id)
      batch.delete(docRef)
    })
    sessions.forEach(({ id }) => {
      const docRef = FirestoreDatabase.collection(Collection.SESSIONS).doc(id)
      batch.delete(docRef)
    })
    const eventDoc = FirestoreDatabase.collection(Collection.EVENTS).doc(eventId)
    batch.delete(eventDoc)

    await batch.commit()
    dispatch(eventSlice.actions.deleteEvent(eventId))
    dispatch(eventSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const resetEventStore = () => (dispatch) => {
  try {
    dispatch(eventSlice.actions.setEvent(null))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
  }
}

export default eventSlice
